import { bn } from 'shared/utils/bignumber'
import { useSWRConfig } from 'swr'
import { useEffect, useMemo } from 'react'
import { useParams, Navigate } from 'react-router'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useSWR } from 'modules/network/hooks/useSwr'
import { useFormConfirmed } from 'shared/hooks/useFormConfirmed'

import {
  SuccessData,
  FormWithdrawalDeposit,
} from 'modules/pools/ui/FormWithdrawalDeposit'
import { SentTransaction } from 'modules/pools/ui/SentTransaction'
import { NarrowWrapper } from 'shared/ui/layout/NarrowWrapper'
import { PageLoader } from 'shared/ui/layout/PageLoader'

import { calcDepositPoolInfo } from 'modules/pools/utils/calcDepositPoolInfo'
import { getPoolInvestorByAddress } from 'modules/pools/utils/getPoolInvestorContract'
import { createRoute } from 'modules/router/utils/createRoute'
import { useAssetsData } from 'modules/pools/hooks/useAssetsData'

function RouteWithdrawalDeposit() {
  const { cache } = useSWRConfig()
  const { chainId, walletAddress } = useWeb3()
  const { poolAddress = '' } = useParams()
  const { getAssetDecimals } = useAssetsData()

  const { pathname, isConfirmed, successData, confirmForm } =
    useFormConfirmed<SuccessData>()
  const depositedAssetDecimals = getAssetDecimals(successData?.formValues.asset)

  const Pool = getPoolInvestorByAddress(chainId, poolAddress)

  const swrKey = `deposit-info-${chainId}-${poolAddress}`
  const deposit = useSWR(swrKey, async () => {
    const pool = Pool.contract.connectRpc({ chainId })
    const res = await calcDepositPoolInfo({
      chainId,
      walletAddress: walletAddress!,
      pool,
    })
    return res
  })

  useEffect(() => {
    return () => {
      cache.delete(swrKey)
    }
  }, [cache, swrKey])

  const sentAmountToDisplay = useMemo(() => {
    if (!successData) return ''
    const { amountPercents, depositedAmount } = successData.formValues
    return new bn(depositedAmount)
      .times(Number(amountPercents) / 100)
      .toFixed(depositedAssetDecimals)
  }, [successData, depositedAssetDecimals])

  if (deposit.isLoading) {
    return <PageLoader />
  }

  if (!deposit.data) {
    return <>Deposit fetching failed</>
  }

  if (isConfirmed) {
    if (!successData) return <Navigate replace to={pathname} />

    return (
      <SentTransaction
        transactionType="Deposit withdrawal"
        txHash={successData.tx.hash}
        amountToDisplay={sentAmountToDisplay}
        amountToken={successData.formValues.asset}
      />
    )
  }

  return (
    <NarrowWrapper>
      <FormWithdrawalDeposit
        risk={Pool.risk}
        asset={Pool.asset}
        depositInfo={deposit.data}
        poolAddress={poolAddress}
        onSuccess={confirmForm}
      />
    </NarrowWrapper>
  )
}

export const routeWithdrawalDeposit = createRoute({
  headerTitle: 'Withdrawal',
  layoutType: 'narrow-extended',
  component: RouteWithdrawalDeposit,
  checkConnect: true,
})
